import RestaurantPlaceholder from '@/components/display/restaurant-placeholder';
import type { Business } from '@/types/business';
import type { MenuItem } from '@/types/menu-item';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import { Image, Pressable, Text, View } from 'react-native';

interface BusinessInfoCardProps {
  menuItem: MenuItem;
  business: Business | undefined;
}

export function BusinessInfoCard({ menuItem, business }: BusinessInfoCardProps) {
  if (!menuItem.businessId) return null;

  const displayName = business?.name || menuItem.businessName || 'Loading...';
  const subtitle = business && (business.cuisineType || business.city)
    ? [
        business.cuisineType,
        business.city && business.state
          ? `${business.city}, ${business.state}`
          : business.city || business.state,
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  return (
    <Pressable
      className="flex-row items-center rounded-lg border border-border bg-surface p-3 mb-3 mt-2"
      style={{ borderCurve: 'continuous' }}
      onPress={() =>
        router.push({
          pathname: '/(root)/business/[id]',
          params: { id: menuItem.businessId },
        })
      }
      accessibilityRole="button"
      accessibilityLabel={`View ${business?.name || menuItem.businessName} details`}
    >
      <View className="rounded-lg overflow-hidden" style={{ width: 48, height: 48, borderCurve: 'continuous' }}>
        {business?.coverPhotoUrl ? (
          <Image
            source={{ uri: imageUrl(business.coverPhotoUrl, 'thumbnail') }}
            style={{ width: 48, height: 48 }}
            resizeMode="cover"
            accessibilityLabel={`Photo of ${business.name}`}
          />
        ) : (
          <RestaurantPlaceholder
            name={business?.name || menuItem.businessName || 'Restaurant'}
            width={48}
            height={48}
          />
        )}
      </View>
      <View className="flex-1 ml-3">
        <Text className="text-base font-semibold text-foreground" numberOfLines={1}>
          {displayName}
        </Text>
        {subtitle && (
          <Text className="text-sm mt-0.5 text-muted-foreground" numberOfLines={1}>
            {subtitle}
          </Text>
        )}
      </View>
      <Ionicons name="chevron-forward" size={20} className="text-muted-foreground" />
    </Pressable>
  );
}
